import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { SlidersHorizontal } from "lucide-react";

interface ExtraFiltersProps {
  rating: string;
  conditions: string;
  onRatingChange: (value: string) => void;
  onConditionsChange: (value: string) => void;
}

export function ExtraFilters({
  rating,
  conditions,
  onRatingChange,
  onConditionsChange,
}: ExtraFiltersProps) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const ratingOptions = ["all", "1", "2", "3", "4", "5"];
  const conditionsOptions = ["all", "1", "2", "3", "4", "5"];

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button 
          variant="outline" 
          size="sm"
          className={`gap-2 ${(rating !== "all" || conditions !== "all") ? "border-apple-blue text-apple-blue" : ""}`}
        >
          <SlidersHorizontal className="h-4 w-4" />
          {t('assets.moreFilters')}
          {(rating !== "all" || conditions !== "all") && (
            <span className="ml-1 rounded-full bg-apple-blue/10 px-2 py-0.5 text-xs">
              {[
                rating !== "all" ? 1 : 0,
                conditions !== "all" ? 1 : 0
              ].reduce((a, b) => a + b, 0)}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-4" align="start">
        <div className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              {t('forms.asset.rating')}
            </label>
            <Select
              value={rating}
              onValueChange={(value) => {
                onRatingChange(value);
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder={t('assets.selectRating')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('assets.allRatings')}</SelectItem>
                {ratingOptions.slice(1).map((value) => (
                  <SelectItem key={value} value={value}>
                    {value} {t('assets.stars')}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              {t('forms.asset.conditions')}
            </label>
            <Select
              value={conditions}
              onValueChange={(value) => {
                onConditionsChange(value);
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder={t('assets.selectCondition')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('assets.allConditions')}</SelectItem>
                {conditionsOptions.slice(1).map((value) => (
                  <SelectItem key={value} value={value}>
                    {t('assets.condition')} {value}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
} 